import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MapPinned, Settings2, MapPin, ChevronDown, Home, Radio } from "lucide-react";
import MapCanvas from "@/components/MapCanvas";
import CategoryFilter from "@/components/CategoryFilter";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { catColor, CategoryIcon } from "@/lib/categories";

const NO_CAT = "Ohne Kategorie";
const catKey = (c) => c || NO_CAT;

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );
}

// Anchored map popup: image (opens full-size in a new tab) + title + category.
function mapPopupHtml(it) {
  const color = catColor(it.category);
  const img = `<a href="${api.imageUrl(it.permid)}" target="_blank" rel="noopener"><img class="go-popup__img" src="${api.imageUrl(
    it.permid,
    400
  )}" alt="" onerror="this.parentNode.style.display='none'"/></a>`;
  const live = it.livePosition
    ? `<div class="go-popup__live" data-testid="map-popup-live">📍 Live-Standort aus Meldung</div>`
    : "";
  return `<div class="go-popup" data-testid="map-popup">${img}<div class="go-popup__body"><div class="go-popup__title">${escapeHtml(
    it.title || "Ohne Titel"
  )}</div><div class="go-popup__cat"><span class="go-popup__dot" style="background:${color}"></span>${escapeHtml(
    it.category || "Ohne Kategorie"
  )}</div>${live}</div></div>`;
}

function useIsDesktop() {
  const query = "(min-width: 1024px)";
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );
  useEffect(() => {
    const m = window.matchMedia(query);
    const handler = (e) => setIsDesktop(e.matches);
    m.addEventListener("change", handler);
    return () => m.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

function within(b, it) {
  if (it.latitude == null || it.longitude == null) return false;
  return (    it.latitude <= b.north &&
    it.latitude >= b.south &&
    it.longitude <= b.east &&
    it.longitude >= b.west
  );
}

function CardThumb({ it, size, canLoad, onSettled }) {
  const [err, setErr] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const done = useRef(false);
  const settle = () => {
    if (!done.current) {
      done.current = true;
      onSettled();
    }
  };
  if (err) {
    return (
      <div
        className={`${size} grid shrink-0 place-items-center rounded-xl`}
        style={{ background: catColor(it.category) }}
      >
        <CategoryIcon category={it.category} color="#fff" size={22} />
      </div>
    );
  }
  return (
    <div className={`${size} relative shrink-0 overflow-hidden rounded-xl bg-slate-100`}>
      {canLoad && (
        <img
          src={api.imageUrl(it.permid, 220)}
          alt=""
          onLoad={() => {
            setLoaded(true);
            settle();
          }}
          onError={() => {
            setErr(true);
            settle();
          }}
          className={`h-full w-full object-cover transition-opacity duration-300 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
    </div>
  );
}

function CategoryLine({ category }) {
  return (
    <p className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-slate-500">      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ background: catColor(category) }}
      />
      {category || "Ohne Kategorie"}
    </p>
  );
}

function convexHull(points) {
  const pts = points
    .map((p) => [p.lng, p.lat])
    .sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (pts.length < 3) return pts.map((p) => [p[1], p[0]]);
  const cross = (o, a, b) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0)
      lower.pop();
    lower.push(p);
  }
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0)
      upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper).map((p) => [p[1], p[0]]);
}

export default function MapsView() {
  const navigate = useNavigate();
  const { items, rootTitle, loading } = useData();
  const isDesktop = useIsDesktop();

  const [bounds, setBounds] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [panTo, setPanTo] = useState(null);
  const [fitNonce, setFitNonce] = useState(0);
  const [searchParams] = useSearchParams();
  const urlFilterApplied = useRef(null);
  const didInitFit = useRef(false);
  const [hidden, setHidden] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("go_maps_hidden_cats") || "[]"));
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    localStorage.setItem("go_maps_hidden_cats", JSON.stringify([...hidden]));
  }, [hidden]);

  // Re-fit the map to the currently active items whenever the filter changes.
  useEffect(() => {
    if (!didInitFit.current) {
      didInitFit.current = true;
      return;
    }
    setFitNonce((n) => n + 1);
  }, [hidden]);

  const listRef = useRef(null);
  const cardRefs = useRef({});
  const scrollLock = useRef(false);
  const scrollTimer = useRef(null);
  const suppressUntil = useRef(0);

  // Sequential image loading: card N loads only after all earlier cards settled.
  const [loadCursor, setLoadCursor] = useState(0);
  const handleSettled = useCallback((idx) => {
    setLoadCursor((c) => Math.max(c, idx + 1));
  }, []);

  // Live GPS positions for the public map (ACT/SOS messages). The backend
  // (/api/map/live) returns overrides for IoT objects (with the "<=30min"
  // rule when they have own coordinates) AND coordless objects that report a
  // GPS fix (shown persistently, no expiry). Identity stays stable when
  // unchanged so the map markers are not rebuilt on every 10s poll.
  const [liveMap, setLiveMap] = useState({});
  const liveSigRef = useRef("");
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.getMapLive();
        if (!active) return;
        const m = {};
        (data.items || []).forEach((o) => {
          if (o.latitude != null && o.longitude != null) {
            m[o.permid] = {
              latitude: o.latitude,
              longitude: o.longitude,
              livePosition: !!o.livePosition,
            };
          }
        });
        const sig = JSON.stringify(m);
        if (sig !== liveSigRef.current) {
          liveSigRef.current = sig;
          setLiveMap(m);
        }
      } catch {
        /* keep previous positions on failure */
      }
    };
    load();
    const id = setInterval(load, 10000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);

  // Live positions (ACT/GPS) arrive a few seconds after the initial map fit.
  // Re-fit ONCE the first time they appear so late-arriving pins (e.g. a
  // coordless ACT-tracked unit far from the other objects) are included in the
  // view and not culled off-screen by the marker clusterer.
  const liveFitDone = useRef(false);
  useEffect(() => {
    if (!liveFitDone.current && Object.keys(liveMap).length > 0) {
      liveFitDone.current = true;
      setFitNonce((n) => n + 1);
    }
  }, [liveMap]);

  const allLocated = useMemo(
    () =>
      (items || [])
        .map((i) => {
          const lp = liveMap[i.permid];
          return lp
            ? {
                ...i,
                latitude: lp.latitude,
                longitude: lp.longitude,
                livePosition: lp.livePosition,
              }
            : i;
        })
        .filter((i) => i.latitude != null && i.longitude != null),
    [items, liveMap]
  );

  const visibleCounts = useMemo(() => {
    const inView = bounds ? allLocated.filter((i) => within(bounds, i)) : allLocated;
    const m = {};
    inView.forEach((i) => {
      const c = catKey(i.category);
      m[c] = (m[c] || 0) + 1;
    });
    return m;
  }, [allLocated, bounds]);

  const categoryOptions = useMemo(() => {
    const m = {};
    allLocated.forEach((i) => {
      const c = catKey(i.category);
      m[c] = (m[c] || 0) + 1;
    });
    return Object.keys(m)
      .sort((a, b) => a.localeCompare(b, "de"))
      .map((name) => ({ name, count: m[name], visibleCount: visibleCounts[name] || 0 }));
  }, [allLocated, visibleCounts]);

  const located = useMemo(
    () => allLocated.filter((i) => !hidden.has(catKey(i.category))),
    [allLocated, hidden]
  );

  // Ring: connect the outermost Terrorsperre pins (convex hull) into a perimeter.
  const ringPoints = useMemo(() => {
    const t = located.filter((i) =>
      catKey(i.category).toLowerCase().startsWith("terrorsperre")
    );
    if (t.length < 3) return [];
    return convexHull(t.map((i) => ({ lat: i.latitude, lng: i.longitude })));
  }, [located]);

  const toggleCat = (name) =>
    setHidden((prev) => {
      const n = new Set(prev);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });
  const showAll = () => setHidden(new Set());
  const hideAll = () => setHidden(new Set(categoryOptions.map((o) => o.name)));

  // Apply ?group=<key> (predefined category sets) or ?filter=prefix1,prefix2 once.
  // Only matching categories stay active; everything else is hidden.
  useEffect(() => {
    if (categoryOptions.length === 0) return;
    const group = (searchParams.get("group") || "").trim().toLowerCase();
    const raw = searchParams.get("filter");
    // Re-apply whenever the group/filter param actually changes (also on SPA nav).
    const sig = `g:${group}|f:${(raw || "").trim().toLowerCase()}`;
    if (!group && !raw) return;
    if (urlFilterApplied.current === sig) return;

    const GROUPS = {
      infrastruktur: (n) => /terrorsperre|baustromverteiler|strom/i.test(n),
      weine: (n) => /weinausschank|sekt/i.test(n),
      herzhaft: (n) =>
        /wurst|grill|fisch|käse|handkäs|kartoffel|flammkuchen|langos|panini|winzerteller|hessisch|spezialit|spieße|burger|tapas/i.test(n),
      suesses: (n) => /crepe|crêpe|schoko|süß|suess|backwaren|gebäck|\beis/i.test(n),
    };

    if (group && GROUPS[group]) {
      urlFilterApplied.current = sig;
      setHidden(new Set(categoryOptions.filter((o) => !GROUPS[group](o.name)).map((o) => o.name)));
      return;
    }

    if (!raw) return;
    const prefixes = raw
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    if (prefixes.length === 0) return;
    urlFilterApplied.current = sig;
    setHidden(
      new Set(
        categoryOptions
          .filter((o) => !prefixes.some((p) => o.name.toLowerCase().startsWith(p)))
          .map((o) => o.name)
      )
    );
  }, [searchParams, categoryOptions]);

  const visible = useMemo(() => {
    if (!bounds) return located;
    return located.filter((i) => within(bounds, i));
  }, [located, bounds]);

  const selectedItem = useMemo(
    () => located.find((i) => i.permid === selectedId) || null,
    [located, selectedId]
  );
  const popupHtml = useMemo(
    () => (selectedItem ? mapPopupHtml(selectedItem) : null),
    [selectedItem]
  );

  // Group the desktop list by category (mobile keeps the flat carousel).
  const groups = useMemo(() => {
    const map = {};
    visible.forEach((it) => {
      const c = catKey(it.category);
      (map[c] = map[c] || []).push(it);
    });
    return Object.keys(map)
      .sort((a, b) => a.localeCompare(b, "de"))
      .map((cat) => ({
        cat,
        items: map[cat].sort((a, b) =>
          (a.title || "").localeCompare(b.title || "", "de")
        ),
      }));
  }, [visible]);

  const groupSig = useMemo(
    () => groups.map((g) => `${g.cat}:${g.items.length}`).join("|"),
    [groups]
  );

  // Desktop list: keep every category group collapsed by default.
  const [expanded, setExpanded] = useState(new Set());
  useEffect(() => {
    setExpanded(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupSig]);

  const toggleGroup = (cat) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(cat) ? n.delete(cat) : n.add(cat);
      return n;
    });

  // Ordered, top-down image loading across the currently rendered cards.
  const orderedItems = useMemo(() => {
    if (isDesktop) {
      const out = [];
      groups.forEach((g) => {
        if (expanded.has(g.cat)) out.push(...g.items);
      });
      return out;
    }
    return visible;
  }, [isDesktop, groups, expanded, visible]);

  const orderIndex = useMemo(() => {
    const m = new Map();
    orderedItems.forEach((it, i) => m.set(it.permid, i));
    return m;
  }, [orderedItems]);

  useEffect(() => {
    setLoadCursor(0);
  }, [orderedItems]);

  useEffect(() => {
    document.title = `${rootTitle || "Weinwoche Wiesbaden 2026"} · Karte`;
  }, [rootTitle]);

  const handleMoveEnd = useCallback((b) => {
    // Ignore programmatic pans triggered by card selection so the result
    // set stays stable while the user browses (only real drags re-filter).
    if (Date.now() < suppressUntil.current) return;
    setBounds(b);
  }, []);

  const select = useCallback(
    (it, { pan = true, scroll = true } = {}) => {
      setSelectedId(it.permid);
      // Ensure the item's category group is open so its card is visible (desktop).
      setExpanded((prev) => {
        const c = catKey(it.category);
        if (prev.has(c)) return prev;
        const n = new Set(prev);
        n.add(c);
        return n;
      });
      if (pan) {
        suppressUntil.current = Date.now() + 1400;
        setPanTo({ lat: it.latitude, lng: it.longitude, keepZoom: true, nonce: Date.now() });
      }
      if (scroll) {
        scrollLock.current = true;
        setTimeout(() => {
          const el = cardRefs.current[it.permid];
          if (el)
            el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
          scrollLock.current = false;
        }, 90);
      }
    },
    []
  );

  const onPinSelect = useCallback((it) => select(it), [select]);

  // Mobile carousel: sync selection + map to the centered card while swiping.
  const onCarouselScroll = useCallback(() => {
    if (isDesktop || scrollLock.current || !listRef.current) return;
    if (scrollTimer.current) clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(() => {
      const c = listRef.current;
      if (!c) return;
      const center = c.scrollLeft + c.clientWidth / 2;
      let best = null;
      let bestDist = Infinity;
      visible.forEach((it) => {
        const el = cardRefs.current[it.permid];
        if (!el) return;
        const elCenter = el.offsetLeft + el.offsetWidth / 2;
        const d = Math.abs(elCenter - center);
        if (d < bestDist) {
          bestDist = d;
          best = it;
        }
      });
      if (best && best.permid !== selectedId) {
        setSelectedId(best.permid);
        suppressUntil.current = Date.now() + 1400;
        setPanTo({ lat: best.latitude, lng: best.longitude, zoom: 18, nonce: Date.now() });
      }
    }, 120);
  }, [isDesktop, visible, selectedId]);

  const homeBtn = (
    <button
      onClick={() => navigate("/")}
      data-testid="home-link"
      aria-label="Zur Startseite"
      className="glass flex h-11 items-center gap-2 rounded-2xl px-3.5 text-sm font-semibold text-primary transition-transform hover:scale-[1.05] active:scale-[0.95]"
    >
      <Home size={17} />
      <span className="hidden sm:inline">Startseite</span>
    </button>
  );

  const adminBtn = (
    <button
      onClick={() => navigate("/admin")}
      data-testid="admin-link"
      aria-label="Verwaltung"
      className="glass grid h-11 w-11 place-items-center rounded-2xl text-slate-600 transition-transform hover:scale-[1.05] active:scale-[0.95]"
    >
      <Settings2 size={18} />
    </button>
  );

  const iotBtn = (
    <button
      onClick={() => navigate("/iot")}
      data-testid="iot-link"
      aria-label="Meldezentrale"
      className="glass grid h-11 w-11 place-items-center rounded-2xl text-rose-600 transition-transform hover:scale-[1.05] active:scale-[0.95]"
    >
      <Radio size={18} />
    </button>
  );

  const filterNode = (
    <CategoryFilter
      options={categoryOptions}
      hidden={hidden}
      onToggle={toggleCat}
      onShowAll={showAll}
      onHideAll={hideAll}
      viewportAware
    />
  );

  const emptyList = !loading && visible.length === 0;

  return (
    <div className="relative flex h-[100dvh] w-full overflow-hidden bg-slate-100">
      {/* Desktop sidebar */}
      {isDesktop && (
        <aside
          className="flex w-[26rem] shrink-0 flex-col border-r border-slate-200 bg-white"
          data-testid="maps-sidebar"
        >
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-primary">
                <MapPinned size={12} /> Event Karte
              </p>
              <h1 className="truncate font-display text-xl font-bold leading-tight text-slate-900">
                {rootTitle || "Weinwoche Wiesbaden 2026"}
              </h1>
              <p className="mt-0.5 text-xs font-semibold uppercase tracking-widest text-slate-400" data-testid="map-result-count">
                {visible.length} Ergebnisse
              </p>
            </div>
            {filterNode}
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto" data-testid="maps-list">
            {loading && (
              <div className="px-4 py-10 text-center text-sm text-slate-400">Lädt…</div>
            )}
            {emptyList && (
              <div className="px-6 py-12 text-center text-sm text-slate-400">
                Keine Objekte im sichtbaren Kartenausschnitt. Zoome heraus oder
                verschiebe die Karte.
              </div>
            )}
            {groups.map((g) => {
              const isOpen = expanded.has(g.cat);
              return (
                <div key={g.cat}>
                  <button
                    onClick={() => toggleGroup(g.cat)}
                    data-testid={`group-header-${g.cat}`}
                    className="sticky top-0 z-10 flex w-full items-center gap-2 border-b border-slate-100 bg-white/95 px-4 py-2.5 text-left backdrop-blur transition-colors hover:bg-slate-50"
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ background: catColor(g.cat) }}
                    />
                    <span className="flex-1 truncate text-xs font-bold uppercase tracking-[0.14em] text-slate-600">
                      {g.cat}
                    </span>
                    <span className="text-xs font-bold text-slate-400">{g.items.length}</span>
                    <ChevronDown
                      size={16}
                      className={`shrink-0 text-slate-400 transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isOpen &&
                    g.items.map((it) => {
                      const idx = orderIndex.get(it.permid) ?? 0;
                      return (
                        <button
                          key={it.permid}
                          ref={(el) => (cardRefs.current[it.permid] = el)}
                          onClick={() => select(it, { scroll: false })}
                          data-testid={`maps-card-${it.permid}`}
                          className={`flex w-full items-center gap-3 border-b border-slate-50 p-3 text-left transition-colors ${
                            selectedId === it.permid ? "bg-amber-50" : "hover:bg-slate-50"
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-display font-semibold text-slate-900">
                              {it.title || "Ohne Titel"}
                            </p>
                          </div>
                          <CardThumb
                            it={it}
                            size="h-[76px] w-[76px]"
                            canLoad={idx <= loadCursor}
                            onSettled={() => handleSettled(idx)}
                          />
                        </button>
                      );
                    })}
                </div>
              );
            })}
          </div>
        </aside>
      )}

      {/* Map */}
      <div className="relative h-[100dvh] flex-1 lg:h-auto">
        <MapCanvas
          className="gomap absolute inset-0 z-0"
          items={located}
          selectedId={selectedId}
          onMoveEnd={handleMoveEnd}
          onSelect={onPinSelect}
          panTo={panTo}
          fitAll={fitNonce}
          hull={ringPoints}
          popupHtml={popupHtml}
          dimUnselected
          cluster
          onBackgroundClick={() => setSelectedId(null)}
          onClusterClick={() => {
            if (listRef.current) {
              listRef.current.scrollTop = 0;
              listRef.current.scrollLeft = 0;
            }
          }}
        />

        {/* Mobile title overlay */}
        {!isDesktop && (
          <div className="glass pointer-events-auto absolute left-3 top-3 z-[500] rounded-2xl px-4 py-2.5">
            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-primary">
              <MapPinned size={12} /> Event Karte
            </p>
            <h1 className="font-display text-lg font-bold leading-tight text-slate-900">
              {rootTitle || "Weinwoche Wiesbaden 2026"}
            </h1>
          </div>
        )}

        {/* Top-right controls: home + admin on desktop; filter + home + admin on mobile */}
        {isDesktop ? (
          <div className="absolute right-3 top-3 z-[500] flex items-center gap-2">
            {homeBtn}
            {iotBtn}
            {adminBtn}
          </div>
        ) : (
          <div className="absolute right-3 top-3 z-[500] flex flex-col items-end gap-2">
            {filterNode}
            {homeBtn}
            {iotBtn}
            {adminBtn}
          </div>
        )}
      </div>

      {/* Mobile horizontal carousel */}
      {!isDesktop && !emptyList && (
        <div
          ref={listRef}
          onScroll={onCarouselScroll}
          data-testid="maps-carousel"
          className="no-scrollbar absolute inset-x-0 bottom-4 z-[500] flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-1"
        >
          {loading && (
            <div className="w-full py-6 text-center text-sm text-slate-500">Lädt…</div>
          )}
          {visible.map((it, index) => (
            <button
              key={it.permid}
              ref={(el) => (cardRefs.current[it.permid] = el)}
              onClick={() => select(it)}
              data-testid={`maps-card-${it.permid}`}
              className={`flex w-[80vw] max-w-sm shrink-0 snap-center items-center gap-3 rounded-2xl bg-white p-3 text-left shadow-xl ring-1 transition-shadow ${
                selectedId === it.permid ? "ring-2 ring-primary" : "ring-slate-100"
              }`}
            >
              <CardThumb
                it={it}
                size="h-16 w-16"
                canLoad={index <= loadCursor}
                onSettled={() => handleSettled(index)}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate font-display font-semibold text-slate-900">
                  {it.title || "Ohne Titel"}
                </p>
                <CategoryLine category={it.category} />
              </div>
              <MapPin size={16} className="shrink-0 text-slate-300" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
